import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { SHAREPOINT_SCOPES } from "@/msal-config"
import {
  SharepointBrowseItem,
  SharepointSite,
} from "@/types/sharepointConnection"
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
} from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"
import { useCallback, useState } from "react"

const GRAPH_BASE_URL = "https://graph.microsoft.com/v1.0"

// MSAL requires window.crypto.subtle which is only available in secure contexts
const isMsalAvailable = !!window.crypto?.subtle

const useSharepointBrowseEnabled = () => {
  const { instance, accounts } = useMsal()
  const { translate } = useTranslation()

  const [sites, setSites] = useState<SharepointSite[]>([])
  const [currentItems, setCurrentItems] = useState<SharepointBrowseItem[]>([])
  const [currentPath, setCurrentPath] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Derive auth state from MSAL accounts (persists across refreshes)
  const isAuthenticated = accounts.length > 0

  const getAccessToken = useCallback(async (): Promise<string> => {
    const account = accounts[0]
    if (!account) {
      throw new Error(translate("SHAREPOINT/ERROR_NO_ACCOUNT"))
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes: SHAREPOINT_SCOPES,
        account,
      })
      return response.accessToken
    } catch (error) {
      // Only show popup for auth errors (token expired, consent required, etc.)
      if (error instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({
          scopes: SHAREPOINT_SCOPES,
        })
        return response.accessToken
      }
      // For other errors (network, permission, etc.), re-throw to be handled by caller
      throw error
    }
  }, [instance, accounts, translate])

  const graphFetch = useCallback(
    async (endpoint: string) => {
      const token = await getAccessToken()
      const response = await fetch(`${GRAPH_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(translate("SHAREPOINT/ERROR_ACCESS_DENIED"))
        }
        if (response.status === 404) {
          throw new Error(translate("SHAREPOINT/ERROR_NOT_FOUND"))
        }
        throw new Error(`Graph API error: ${response.status}`)
      }

      return response.json()
    },
    [getAccessToken, translate],
  )

  const login = useCallback(async (): Promise<AuthenticationResult> => {
    try {
      setLoading(true)
      setError(null)
      return await instance.loginPopup({
        scopes: SHAREPOINT_SCOPES,
        prompt: "select_account",
      })
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate("SHAREPOINT/ERROR_LOGIN_FAILED"),
      )
      throw err
    } finally {
      setLoading(false)
    }
  }, [instance, translate])

  const logout = useCallback(async () => {
    setSites([])
    setCurrentItems([])
    setCurrentPath([])
    setError(null)
    setLoading(false)
  }, [])

  const loadSites = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await graphFetch(
        "/sites?search=*&$select=id,displayName,webUrl,description&$top=100",
      )
      const mappedSites: SharepointSite[] = (data.value || []).map(
        (site: Record<string, string>) => ({
          id: site.id || "",
          name:
            site.displayName ||
            site.name ||
            translate("SHAREPOINT/UNNAMED_SITE"),
          webUrl: site.webUrl || "",
          description: site.description || undefined,
        }),
      )
      setSites(mappedSites)
      return mappedSites
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : translate("SHAREPOINT/ERROR_LOAD_SITES")
      setError(message)
      return []
    } finally {
      setLoading(false)
    }
  }, [graphFetch, translate])

  const browseFolderContents = useCallback(
    async (siteId: string, path: string = "/") => {
      try {
        setLoading(true)
        setError(null)

        const cleanPath = path.startsWith("/") ? path : `/${path}`
        const endpoint =
          cleanPath === "/"
            ? `/sites/${siteId}/drive/root/children`
            : `/sites/${siteId}/drive/root:${cleanPath}:/children`

        const data = await graphFetch(
          `${endpoint}?$select=id,name,size,lastModifiedDateTime,webUrl,file,folder&$top=500`,
        )

        const items: SharepointBrowseItem[] = (data.value || []).map(
          (item: Record<string, unknown>) => ({
            id: item.id as string,
            name: item.name as string,
            isFolder: !!item.folder,
            size: (item.size as number) || 0,
            childCount: item.folder
              ? (item.folder as Record<string, number>).childCount
              : undefined,
            webUrl: item.webUrl as string,
            lastModifiedDateTime: item.lastModifiedDateTime as string,
          }),
        )

        // Sort: folders first, then files, both alphabetically
        items.sort((a, b) => {
          if (a.isFolder && !b.isFolder) return -1
          if (!a.isFolder && b.isFolder) return 1
          return a.name.localeCompare(b.name)
        })

        setCurrentItems(items)
        return items
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : translate("SHAREPOINT/ERROR_LOAD_FOLDER")
        setError(message)
        return []
      } finally {
        setLoading(false)
      }
    },
    [graphFetch, translate],
  )

  const clearError = useCallback(() => setError(null), [])

  return {
    sites,
    currentItems,
    currentPath,
    setCurrentPath,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    getAccessToken,
    graphFetch,
    loadSites,
    browseFolderContents,
    clearError,
  }
}

export type SharepointBrowseHook = ReturnType<typeof useSharepointBrowseEnabled>

/**
 * No-op version of the hook used when MsalProvider is absent (non-secure context).
 * Keeps the same hooks call-order so React rules are satisfied.
 */
const useSharepointBrowseDisabled = (): SharepointBrowseHook => {
  const { translate } = useTranslation()
  const [sites] = useState<SharepointSite[]>([])
  const [currentItems] = useState<SharepointBrowseItem[]>([])
  const [currentPath, setCurrentPath] = useState<string[]>([])
  const [loading] = useState(false)
  const [error] = useState<string | null>(null)

  const noop = useCallback(() => {
    throw new Error(translate("SHAREPOINT/ERROR_UNAVAILABLE"))
  }, [translate])

  return {
    sites,
    currentItems,
    currentPath,
    setCurrentPath,
    loading,
    error,
    isAuthenticated: false,
    login: noop as unknown as () => Promise<AuthenticationResult>,
    logout: useCallback(async () => {}, []),
    getAccessToken: noop as unknown as () => Promise<string>,
    graphFetch: noop as unknown as (endpoint: string) => Promise<unknown>,
    loadSites: useCallback(async () => [] as SharepointSite[], []),
    browseFolderContents: useCallback(
      async () => [] as SharepointBrowseItem[],
      [],
    ),
    clearError: useCallback(() => {}, []),
  }
}

// Module-level selection: chosen once at load time, so hooks call-order is stable
export const useSharepointBrowse = isMsalAvailable
  ? useSharepointBrowseEnabled
  : useSharepointBrowseDisabled
